const STORAGE_KEY = "improve_onboarding_wizard_v1";
const COMPLETED_KEY = "improve_onboarding_wizard_completed";

export const TOUR_FLAG_KEYS = [
  "tourCompleted",
  "tourSkipped",
  "webinarOffered",
  "webinarDismissed"
];

function currentAccountSuffix() {
  try {
    const vuex = JSON.parse(localStorage.getItem("vuex") || "{}");
    const user = vuex.auth && vuex.auth.user ? vuex.auth.user : {};
    const id = user.company_id || user.id;
    return id ? "_" + id : "";
  } catch (e) {
    return "";
  }
}

export function getOnboardingStorageKey() {
  return STORAGE_KEY + currentAccountSuffix();
}

function getCompletedKey() {
  return COMPLETED_KEY + currentAccountSuffix();
}

export function getCachedOnboarding() {
  try {
    const raw = localStorage.getItem(getOnboardingStorageKey());
    if (!raw) return {};
    const saved = JSON.parse(raw);
    return saved && typeof saved === "object" ? saved : {};
  } catch (e) {
    return {};
  }
}

export function extractTourFlags(data) {
  const src = data && typeof data === "object" ? data : {};
  return {
    tourCompleted: !!src.tourCompleted,
    tourSkipped: !!src.tourSkipped,
    webinarOffered: !!src.webinarOffered,
    webinarDismissed: !!src.webinarDismissed
  };
}

export function mergeTourFlags(base, incoming) {
  const a = extractTourFlags(base);
  const b = extractTourFlags(incoming);
  return {
    tourCompleted: a.tourCompleted || b.tourCompleted,
    tourSkipped: a.tourSkipped || b.tourSkipped,
    webinarOffered: a.webinarOffered || b.webinarOffered,
    webinarDismissed: a.webinarDismissed || b.webinarDismissed
  };
}

/**
 * Per-chapter guided-tour state: { core: { completed, step }, ... }.
 * PHP's json_encode turns an empty set into [] rather than {}, so an array
 * from the server must read as "no chapters", not garbage.
 */
export function extractTourChapters(data) {
  const src = data && typeof data === "object" ? data : getCachedOnboarding();
  const ch = src.tourChapters;
  if (!ch || typeof ch !== "object" || Array.isArray(ch)) return {};
  return ch;
}

export function mergeTourChapters(base, incoming) {
  const a = extractTourChapters(base && typeof base === "object" ? base : {});
  const b = extractTourChapters(
    incoming && typeof incoming === "object" ? incoming : {}
  );
  const out = {};
  Object.keys(a).forEach(function(id) {
    out[id] = a[id];
  });
  Object.keys(b).forEach(function(id) {
    const prev = out[id] || {};
    out[id] = {
      // Completion never un-happens from a stale write, same rule as the
      // tour flags above.
      completed: !!prev.completed || !!b[id].completed,
      step:
        typeof b[id].step === "number"
          ? b[id].step
          : typeof prev.step === "number"
          ? prev.step
          : 0
    };
  });
  return out;
}

export function isTourFinished(data) {
  const flags = extractTourFlags(data || getCachedOnboarding());
  return flags.tourCompleted || flags.tourSkipped;
}

export function isTrialCompanyAdmin(storeGetters) {
  if (!storeGetters) return false;
  if (process.env.VUE_APP_NAME && process.env.VUE_APP_NAME !== "direct") {
    return false;
  }
  const state = Number(storeGetters.companyState);
  const level = Number(storeGetters.level);
  const isAdmin = storeGetters.admin === true || level === 2;
  return (
    (state === 1 || state === 5) &&
    isAdmin &&
    !storeGetters.superAdmin &&
    !storeGetters.partner
  );
}

export function shouldShowProductTour(storeGetters, progress) {
  if (!isTrialCompanyAdmin(storeGetters)) return false;
  return !isTourFinished(progress || getCachedOnboarding());
}

export function mergeOnboardingPayload(updates) {
  const cached = getCachedOnboarding();
  const payload = Object.assign(
    {},
    cached,
    updates || {},
    mergeTourFlags(cached, updates)
  );
  // Object.assign would let an incoming tourChapters replace the cached set
  // wholesale, losing other chapters' progress - merge per chapter instead.
  // Only attach the key when there is actual chapter state: the server also
  // merges per chapter (completion is sticky), but an unrelated save (wizard
  // step, webinar flag) has no business carrying this key at all.
  const chapters = mergeTourChapters(cached, updates);
  if (Object.keys(chapters).length) {
    payload.tourChapters = chapters;
  } else {
    delete payload.tourChapters;
  }
  if (cached.completed) payload.completed = true;
  cacheOnboardingLocally(payload);
  return payload;
}

export function isOnboardingCompleted() {
  try {
    if (localStorage.getItem(getCompletedKey()) === "true") return true;
    const raw = localStorage.getItem(getOnboardingStorageKey());
    if (!raw) return false;
    const saved = JSON.parse(raw);
    return !!(saved && saved.completed);
  } catch (e) {
    return false;
  }
}

export function markOnboardingCompleted() {
  localStorage.setItem(getCompletedKey(), "true");
  try {
    const raw = localStorage.getItem(getOnboardingStorageKey());
    const saved = raw ? JSON.parse(raw) : {};
    saved.completed = true;
    localStorage.setItem(getOnboardingStorageKey(), JSON.stringify(saved));
  } catch (e) {
    // ignore
  }
}

export function resetOnboardingProgress() {
  localStorage.removeItem(getCompletedKey());
  localStorage.removeItem(getOnboardingStorageKey());
  localStorage.removeItem(COMPLETED_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

export function shouldShowOnboardingOnLogin() {
  return !isOnboardingCompleted();
}

export function cacheOnboardingLocally(payload) {
  localStorage.setItem(getOnboardingStorageKey(), JSON.stringify(payload));
  if (payload && payload.completed) {
    localStorage.setItem(getCompletedKey(), "true");
  } else {
    localStorage.removeItem(getCompletedKey());
  }
}

export default {
  STORAGE_KEY,
  COMPLETED_KEY,
  TOUR_FLAG_KEYS,
  getOnboardingStorageKey,
  getCachedOnboarding,
  extractTourFlags,
  mergeTourFlags,
  extractTourChapters,
  mergeTourChapters,
  isTourFinished,
  isTrialCompanyAdmin,
  shouldShowProductTour,
  mergeOnboardingPayload,
  isOnboardingCompleted,
  markOnboardingCompleted,
  resetOnboardingProgress,
  shouldShowOnboardingOnLogin,
  cacheOnboardingLocally
};
